import _extends from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/extends.js";
var __jsx = React.createElement;
import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import { faCheck, faChevronDown, faChevronRight, faSpinner, faStars } from "@fortawesome/pro-regular-svg-icons";
import AwesomeIcon from "../../AwesomeIcon";

/**
 * Assistant bubble for the orqestrator chat.
 *
 * Markup and classes deliberately mirror CustomAssistantMessage so the two
 * chats are visually indistinguishable. Not shared as one component yet because
 * that one calls useCopilotChat, which ties it to CopilotKit's message store.
 *
 * Still missing versus the CopilotKit bubble: FeedbackControls (thumbs, copy,
 * regenerate) and the PatientSummary card for <<<PATIENT_SUMMARY_JSON>>>
 * replies. Both need the shared-component split first.
 */

/** `set_patient_custom_tag` -> `Set patient custom tag`. Tool names are wire
 *  identifiers; a clinician should not have to read snake_case. */
function toolLabel(name) {
  var words = name.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Pretty-print JSON when it is JSON, otherwise show the raw text. Tool output
 *  is not guaranteed to be either. */
function formatPayload(raw) {
  var text = raw.trim();
  if (!text) return "(empty)";
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch (_unused) {
    return text;
  }
}

/**
 * One tool the agent ran, collapsed by default.
 *
 * Collapsed because the answer is the point and the working is the detail: a
 * clinician reading a reply should not have to scroll past a JSON blob to find
 * it. Expandable because a reply that depends on a tool is hard to trust
 * otherwise — if the agent says there are no reminders, it matters whether it
 * checked, what it asked, and what came back.
 */
var ToolRow = function ToolRow(_ref) {
  var tool = _ref.tool;
  var _useState = useState(false),
    open = _useState[0],
    setOpen = _useState[1];
  var hasDetail = Boolean(tool.args || tool.result !== undefined);
  return /*#__PURE__*/React.createElement("div", {
    className: "rounded-md border border-gray-200 bg-gray-50 text-xs dark:border-gray-700 dark:bg-gray-900"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: function onClick() {
      return hasDetail && setOpen(function (v) {
        return !v;
      });
    },
    "aria-expanded": open,
    disabled: !hasDetail,
    className: "flex w-full items-center gap-2 px-3 py-2 text-left text-gray-600 dark:text-gray-300 ".concat(hasDetail ? "hover:bg-gray-100 dark:hover:bg-gray-800" : "cursor-default")
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: tool.done ? faCheck : faSpinner,
    size: "sm",
    className: tool.done ? "text-teal-500" : "animate-spin"
  }), /*#__PURE__*/React.createElement("span", {
    className: "flex-1"
  }, tool.done ? "Used" : "Using", " ", toolLabel(tool.name)), hasDetail && /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: open ? faChevronDown : faChevronRight,
    size: "sm"
  })), open && /*#__PURE__*/React.createElement("div", {
    className: "border-t border-gray-200 px-3 py-2 dark:border-gray-700"
  }, tool.args && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "mb-1 font-semibold text-gray-500 dark:text-gray-400"
  }, "Sent"), /*#__PURE__*/React.createElement("pre", {
    className: "mb-2 overflow-x-auto whitespace-pre-wrap break-words text-gray-700 dark:text-gray-200"
  }, formatPayload(tool.args))), tool.result !== undefined && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "mb-1 font-semibold text-gray-500 dark:text-gray-400"
  }, "Returned"), /*#__PURE__*/React.createElement("pre", {
    className: "overflow-x-auto whitespace-pre-wrap break-words text-gray-700 dark:text-gray-200"
  }, formatPayload(tool.result)))));
};
var ToolTrace = function ToolTrace(_ref2) {
  var tools = _ref2.tools;
  return /*#__PURE__*/React.createElement("div", {
    className: "mb-3 flex flex-col gap-1 last:mb-0"
  }, tools.map(function (tool) {
    return /*#__PURE__*/React.createElement(ToolRow, {
      key: tool.id,
      tool: tool
    });
  }));
};
var LOADING_PHRASES = ["Analyzing clinical context...", "Compiling patient data...", "Processing medical records...", "Interpreting inputs...", "Reviewing patient information...", "Gathering relevant details...", "Synchronizing with medical database..."];

/**
 * Element styling for rendered markdown.
 *
 * Written out rather than using `prose` classes: @tailwindcss/typography is not
 * installed, so those are inert. Adding it would mean a new dependency and a
 * change to the app-wide tailwind config, for the benefit of one bubble.
 *
 * Sized to sit inside a narrow sidebar: a reply's `#` heading should read as a
 * section label, not a page title.
 */
var MARKDOWN_COMPONENTS = {
  h1: function h1(p) {
    return /*#__PURE__*/React.createElement("h3", _extends({
      className: "mb-2 mt-3 text-base font-semibold first:mt-0"
    }, p));
  },
  h2: function h2(p) {
    return /*#__PURE__*/React.createElement("h4", _extends({
      className: "mb-2 mt-3 text-base font-semibold first:mt-0"
    }, p));
  },
  h3: function h3(p) {
    return /*#__PURE__*/React.createElement("h5", _extends({
      className: "mb-1 mt-3 text-sm font-semibold first:mt-0"
    }, p));
  },
  p: function p(_p) {
    return /*#__PURE__*/React.createElement("p", _extends({
      className: "mb-2 last:mb-0"
    }, _p));
  },
  ul: function ul(p) {
    return /*#__PURE__*/React.createElement("ul", _extends({
      className: "mb-2 list-disc space-y-0.5 pl-5 last:mb-0"
    }, p));
  },
  ol: function ol(p) {
    return /*#__PURE__*/React.createElement("ol", _extends({
      className: "mb-2 list-decimal space-y-0.5 pl-5 last:mb-0"
    }, p));
  },
  li: function li(p) {
    return /*#__PURE__*/React.createElement("li", _extends({
      className: "leading-snug"
    }, p));
  },
  strong: function strong(p) {
    return /*#__PURE__*/React.createElement("strong", _extends({
      className: "font-semibold"
    }, p));
  },
  em: function em(p) {
    return /*#__PURE__*/React.createElement("em", _extends({
      className: "italic"
    }, p));
  },
  a: function a(p) {
    return /*#__PURE__*/React.createElement("a", _extends({
      className: "text-teal-700 underline dark:text-teal-300",
      target: "_blank",
      rel: "noreferrer noopener"
    }, p));
  },
  code: function code(p) {
    return /*#__PURE__*/React.createElement("code", _extends({
      className: "rounded bg-gray-200 px-1 py-0.5 font-mono text-[0.85em] dark:bg-gray-700"
    }, p));
  },
  pre: function pre(p) {
    return /*#__PURE__*/React.createElement("pre", _extends({
      className: "mb-2 overflow-x-auto rounded bg-gray-200 p-3 text-[0.85em] last:mb-0 dark:bg-gray-900"
    }, p));
  },
  blockquote: function blockquote(p) {
    return /*#__PURE__*/React.createElement("blockquote", _extends({
      className: "mb-2 border-l-2 border-gray-400 pl-3 italic last:mb-0"
    }, p));
  },
  hr: function hr() {
    return /*#__PURE__*/React.createElement("hr", {
      className: "my-3 border-gray-300 dark:border-gray-600"
    });
  },
  table: function table(p) {
    return /*#__PURE__*/React.createElement("div", {
      className: "mb-2 overflow-x-auto last:mb-0"
    }, /*#__PURE__*/React.createElement("table", _extends({
      className: "w-full text-left text-[0.9em]"
    }, p)));
  },
  th: function th(p) {
    return /*#__PURE__*/React.createElement("th", _extends({
      className: "border-b border-gray-400 py-1 pr-3 font-semibold"
    }, p));
  },
  td: function td(p) {
    return /*#__PURE__*/React.createElement("td", _extends({
      className: "py-1 pr-3 align-top"
    }, p));
  }
};
var AssistantMessage = function AssistantMessage(_ref3) {
  var content = _ref3.content,
    streaming = _ref3.streaming,
    failed = _ref3.failed,
    tools = _ref3.tools;
  // Chosen once per message. CustomAssistantMessage picks this inline on every
  // render, so its phrase changes on each re-render; with streaming that would
  // flicker on every delta.
  var _useState2 = useState(function () {
      return LOADING_PHRASES[Math.floor(Math.random() * LOADING_PHRASES.length)];
    }),
    phrase = _useState2[0];

  // Nothing has arrived yet and no tool is running: show the thinking indicator
  // instead of an empty bubble, matching how the CopilotKit chat handles its
  // loading state. Once a tool starts, fall through to the bubble below — the
  // trace is more informative than a generic phrase, and opening the bubble
  // early means it does not jump when the reply text starts arriving.
  if (streaming && !content && !(tools !== null && tools !== void 0 && tools.length)) {
    return /*#__PURE__*/React.createElement("div", {
      className: "flex space-x-3 pl-8 text-gray-600 dark:text-gray-400"
    }, /*#__PURE__*/React.createElement(AwesomeIcon, {
      name: faSpinner,
      size: "lg",
      className: "animate-spin"
    }), /*#__PURE__*/React.createElement("p", {
      className: "text-md italic"
    }, phrase));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "mb-4 flex items-start justify-start last:mb-0"
  }, /*#__PURE__*/React.createElement("div", {
    className: "w-full max-w-[90%]"
  }, /*#__PURE__*/React.createElement("div", {
    className: "inline-block"
  }, /*#__PURE__*/React.createElement("div", {
    className: "text-md flex w-fit items-center gap-x-2 rounded-t-lg bg-gray-500 px-4 py-2 font-semibold text-teal-200 dark:bg-gray-800 dark:text-teal-300"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faStars,
    size: "lg",
    className: "text-teal-200 dark:text-teal-300"
  }), "AI Assistant"), /*#__PURE__*/React.createElement("div", {
    className: "rounded-b-lg bg-gray-100 px-5 py-4 text-gray-700 dark:bg-gray-800 dark:text-white ".concat(failed ? "border border-gray-400 dark:border-gray-600" : "")
  }, tools !== null && tools !== void 0 && tools.length ? /*#__PURE__*/React.createElement(ToolTrace, {
    tools: tools
  }) : null, content ? /*#__PURE__*/React.createElement(ReactMarkdown, {
    components: MARKDOWN_COMPONENTS
  }, content) : null))));
};
export default AssistantMessage;